import type { Component, Page, Project } from "../../types";
import { generateId } from "../../utils";
import { ComponentPersistenceValidator } from "../../utils/componentPersistenceValidator";
import { migrateLegacyTemplateComponents } from "../../utils/componentTreeMigrations";
import { migrateProjectThemeColors } from "../../utils/project-theme-color-migration";
import { rebuildParentIds } from "../../utils/rebuildParentIds";
import { responsivePositionTranslator } from "../../services/responsive/ResponsivePositionTranslator";
import { enhanceComponentsWithChildren } from "./editor-utils";
import {
  createDefaultPage,
  getLegacyRootComponents,
  hydrateProjectComponent,
  isRecord,
  normalizePageCanvasSettings,
  toIdMap,
} from "./editor-store-helpers";
import type {
  ProjectSliceState,
  VersionSnapshotCandidate,
  WrappedPage,
} from "./project-slice-types";

function buildPagesFromSnapshot(
  project: Project,
  snapshotPages: unknown[],
  fallbackCanvasSettings?: Page["canvasSettings"],
): Page[] {
  return snapshotPages
    .filter(isRecord)
    .map((pageSnapshot, index) => {
      const snapshotPageId =
        typeof pageSnapshot.pageId === "string"
          ? pageSnapshot.pageId
          : typeof pageSnapshot.id === "string"
            ? pageSnapshot.id
            : null;

      if (!snapshotPageId) {
        return null;
      }

      const basePage = createDefaultPage(
        project.id,
        project.ownerId || "unknown",
        fallbackCanvasSettings,
      );

      return {
        ...basePage,
        id: snapshotPageId,
        name:
          typeof pageSnapshot.pageName === "string" && pageSnapshot.pageName.trim().length > 0
            ? pageSnapshot.pageName
            : index === 0
              ? "Home"
              : `Page ${index + 1}`,
        order: index,
        isDefault: index === 0,
        canvasSettings: normalizePageCanvasSettings(
          isRecord(pageSnapshot.canvasSettings)
            ? (pageSnapshot.canvasSettings as Partial<Page["canvasSettings"]>)
            : fallbackCanvasSettings,
        ),
      } satisfies Page;
    })
    .filter((page): page is Page => page !== null);
}

export function isValidPage(value: unknown): value is Page {
  return isRecord(value) && typeof value.id === "string";
}

export function isWrappedPage(value: unknown): value is WrappedPage {
  return isRecord(value) && isValidPage(value.page);
}

export function isVersionSnapshotCandidate(
  value: unknown,
): value is VersionSnapshotCandidate {
  return isRecord(value) && typeof value.version === "number";
}

export function getCurrentVersionCandidate(
  project: Project,
): VersionSnapshotCandidate | null {
  const versionsValue: unknown = project.versions;

  if (Array.isArray(versionsValue)) {
    if (versionsValue.length === 0) {
      return null;
    }

    const currentVersion =
      versionsValue.find((version) => version.version === project.currentVersion) ||
      versionsValue.reduce((latest, version) =>
        version.version > latest.version ? version : latest,
      );

    return {
      version: currentVersion.version,
      snapshot: currentVersion.snapshot,
    };
  }

  if (!isRecord(versionsValue)) {
    return null;
  }

  const currentCandidate = versionsValue[String(project.currentVersion)];
  if (isVersionSnapshotCandidate(currentCandidate)) {
    return currentCandidate;
  }

  const latestKey = Object.keys(versionsValue)
    .map(Number)
    .filter(Number.isFinite)
    .sort((left, right) => right - left)[0];

  if (latestKey === undefined) {
    return null;
  }

  const latestCandidate = versionsValue[String(latestKey)];
  return isVersionSnapshotCandidate(latestCandidate)
    ? latestCandidate
    : null;
}

export function normalizeLoadedComponents(components: Component[]): Component[] {
  return enhanceComponentsWithChildren(
    migrateProjectThemeColors(migrateLegacyTemplateComponents(components)),
  );
}

export function hydrateComponentSnapshots(
  components: unknown[],
  projectId: string,
  pageId: string,
  ownerId: string,
): Component[] {
  return normalizeLoadedComponents(
    components
      .filter(isRecord)
      .map((component) =>
        hydrateProjectComponent(component, projectId, pageId, ownerId),
      )
      .filter((component): component is Component => component !== null),
  );
}

export function setActivePageFromLoadedState(
  state: ProjectSliceState,
  pageId: string,
): void {
  const pageComponents = state.pageComponents.get(pageId) || [];
  const validComponents = pageComponents.filter(
    (component) => component && component.id,
  );
  const rebuiltComponents = rebuildParentIds(validComponents);
  const enhancedComponents = enhanceComponentsWithChildren(rebuiltComponents);

  state.currentPageId = pageId;
  state.currentPage = state.pageMap.get(pageId) || null;
  state.components = [...enhancedComponents];
  state.componentMap = toIdMap(enhancedComponents);

  const pageHistory = state.pageHistoryMap.get(pageId);
  state.history = pageHistory || {
    past: [],
    present: [...enhancedComponents],
    future: [],
  };

  if (!pageHistory) {
    state.pageHistoryMap.set(pageId, state.history);
  }

  state.lastUpdateTimestamp = Date.now();
  state.isModified = false;
  state.selectedComponents = [];
  state.hoveredComponent = undefined;
  state.canvasSelected = false;
}

export function loadProjectIntoState(
  state: ProjectSliceState,
  loadedProject: Project,
): string {
  // Existing project color migration is intentionally in-memory for now.
  // TODO: persist themeColorMigrationVersion only through an explicit save/migration flow.
  const project = migrateProjectThemeColors(loadedProject);

  state.components = [];
  state.componentMap = new Map();
  state.selectedComponents = [];
  state.hoveredComponent = undefined;
  state.pageComponents = new Map();
  state.pageHistoryMap = new Map();
  state.pages = [];
  state.pageMap = new Map();
  state.currentPageId = undefined;
  state.currentPage = null;
  state.history = { past: [], present: [], future: [] };

  state.project = project;
  state.isModified = false;
  state.lastSaved = new Date();
  state.versioningEnabled = true;
  state.lastVersionSnapshot =
    project.versions.length > 0
      ? new Date(
          Math.max(
            ...project.versions.map((version) =>
              new Date(version.createdAt).getTime(),
            ),
          ),
        )
      : null;

  const currentVersion = getCurrentVersionCandidate(project);

  if (!project.pages || project.pages.length === 0) {
    const snapshotPages = Array.isArray(currentVersion?.snapshot?.pages)
      ? currentVersion.snapshot.pages
      : [];
    const fallbackPages = buildPagesFromSnapshot(
      project,
      snapshotPages,
      state.canvasSettings,
    );

    if (fallbackPages.length > 0) {
      state.pages = fallbackPages;
      state.pageMap = toIdMap(fallbackPages);
    } else {
      const defaultPage = createDefaultPage(
        project.id,
        project.ownerId || "unknown",
        state.canvasSettings,
      );

      state.pages = [defaultPage];
      state.pageMap = toIdMap([defaultPage]);
    }
  } else {
    const unwrappedPages = project.pages
      .map((pageLike) => {
        if (isWrappedPage(pageLike)) {
          return pageLike.page;
        }
        return isValidPage(pageLike) ? pageLike : null;
      })
      .filter((page): page is Page => page !== null);

    if (unwrappedPages.length > 0) {
      state.pages = unwrappedPages;
      state.pageMap = toIdMap(unwrappedPages);
    } else {
      const snapshotPages = Array.isArray(currentVersion?.snapshot?.pages)
        ? currentVersion.snapshot.pages
        : [];
      const fallbackPages = buildPagesFromSnapshot(
        project,
        snapshotPages,
        state.canvasSettings,
      );

      if (fallbackPages.length > 0) {
        console.debug(
          "[EDITOR_HYDRATION] Falling back to snapshot-defined pages because project.pages was malformed",
          {
            projectId: project.id,
            rawPageCount: project.pages.length,
            snapshotPageCount: fallbackPages.length,
          },
        );
        state.pages = fallbackPages;
        state.pageMap = toIdMap(fallbackPages);
      } else {
        state.pages = [];
        state.pageMap = new Map();
      }
    }
  }

  let hasLoadedComponentsFromSnapshots = false;
  let componentLoadingStrategy = "none";

  if (currentVersion?.snapshot) {
    const snapshot = currentVersion.snapshot;

    if (Array.isArray(snapshot.pages)) {
      componentLoadingStrategy = "multi-page-snapshots";

      snapshot.pages.forEach((pageSnapshot) => {
        if (!isRecord(pageSnapshot)) {
          return;
        }

        const pageId =
          typeof pageSnapshot.pageId === "string"
            ? pageSnapshot.pageId
            : typeof pageSnapshot.id === "string"
              ? pageSnapshot.id
              : undefined;
        const pageComponents = Array.isArray(pageSnapshot.components)
          ? hydrateComponentSnapshots(
              pageSnapshot.components,
              project.id,
              pageId || "",
              project.ownerId || "unknown",
            )
          : [];

        if (pageId) {
          state.pageComponents.set(pageId, pageComponents);
          state.pageHistoryMap.set(pageId, {
            past: [],
            present: [...pageComponents],
            future: [],
          });

          if (pageComponents.length > 0) {
            hasLoadedComponentsFromSnapshots = true;
          }
        }
      });
    } else if (Array.isArray(snapshot.components)) {
      componentLoadingStrategy = "legacy-snapshots";

      const defaultPage = state.pages.find((page) => page.isDefault);
      if (defaultPage) {
        const legacyComponents = snapshot.components
          .filter(isRecord)
          .map((component) =>
            hydrateProjectComponent(
              { ...component, pageId: defaultPage.id },
              project.id,
              defaultPage.id,
              project.ownerId || "unknown",
            ),
          )
          .filter((component): component is Component => component !== null);

        const normalizedComponents = normalizeLoadedComponents(legacyComponents);
        state.pageComponents.set(defaultPage.id, normalizedComponents);
        state.pageHistoryMap.set(defaultPage.id, {
          past: [],
          present: [...normalizedComponents],
          future: [],
        });
        hasLoadedComponentsFromSnapshots = normalizedComponents.length > 0;
      }
    }
  }

  if (!hasLoadedComponentsFromSnapshots) {
    componentLoadingStrategy = "direct-project-data";
    const legacyComponents = getLegacyRootComponents(project);

    if (legacyComponents && legacyComponents.length > 0) {
      const defaultPage =
        state.pages.find((page) => page.isDefault) || state.pages[0];

      if (defaultPage) {
        const hydratedComponents = normalizeLoadedComponents(
          legacyComponents
            .map((component) =>
              hydrateProjectComponent(
                component,
                project.id,
                defaultPage.id,
                project.ownerId || "unknown",
              ),
            )
            .filter((component): component is Component => component !== null),
        );

        state.pageComponents.set(defaultPage.id, hydratedComponents);
        state.pageHistoryMap.set(defaultPage.id, {
          past: [],
          present: [...hydratedComponents],
          future: [],
        });
        hasLoadedComponentsFromSnapshots = hydratedComponents.length > 0;
      }
    }

    if (!hasLoadedComponentsFromSnapshots && Array.isArray(project.pages)) {
      state.pages.forEach((page) => {
        const projectPage = project.pages.find(
          (candidate) => candidate.id === page.id,
        );
        const embeddedComponents =
          projectPage &&
          isRecord(projectPage) &&
          Array.isArray(projectPage.components)
            ? hydrateComponentSnapshots(
                projectPage.components,
                project.id,
                page.id,
                project.ownerId || "unknown",
              )
            : [];

        state.pageComponents.set(page.id, embeddedComponents);
        state.pageHistoryMap.set(page.id, {
          past: [],
          present: [...embeddedComponents],
          future: [],
        });

        if (embeddedComponents.length > 0) {
          hasLoadedComponentsFromSnapshots = true;
        }
      });
    }
  } else {
    state.pages.forEach((page) => {
      const existingComponents = state.pageComponents.get(page.id) || [];

      if (existingComponents.length > 0) {
        const hydratedComponents = normalizeLoadedComponents(
          existingComponents.map((component) => ({
            ...component,
            id: component.id || generateId("component"),
            pageId: page.id,
            createdAt: component.createdAt
              ? new Date(component.createdAt)
              : new Date(),
            updatedAt: component.updatedAt
              ? new Date(component.updatedAt)
              : new Date(),
            properties: component.properties || {},
            style: component.style || {},
            position: component.position || { x: 0, y: 0, z: 0 },
            size: component.size || { width: 200, height: 100 },
            children: component.children || [],
            parentId: component.parentId || undefined,
            locked: component.locked || false,
            hidden: component.hidden || false,
            order: component.order || 0,
            name: component.name || `${component.type} Component`,
            responsivePosition:
              component.responsivePosition ||
              responsivePositionTranslator.generateDefaultResponsivePosition(
                component.type,
              ),
          })),
        );

        state.pageComponents.set(page.id, hydratedComponents);
        const pageHistory = state.pageHistoryMap.get(page.id);
        if (pageHistory) {
          pageHistory.present = hydratedComponents;
        }
      } else if (!state.pageComponents.has(page.id)) {
        state.pageComponents.set(page.id, []);
        state.pageHistoryMap.set(page.id, {
          past: [],
          present: [],
          future: [],
        });
      }
    });
  }

  const defaultPageId =
    project.currentPageId || state.pages.find((page) => page.isDefault)?.id;

  if (defaultPageId) {
    setActivePageFromLoadedState(state, defaultPageId);
  }

  const persistenceReport = ComponentPersistenceValidator.validateProject(
    project,
    componentLoadingStrategy,
  );

  if (persistenceReport.validation.errors.length > 0) {
    console.error(
      "🚨 [EDITOR_DEBUG] Component persistence validation failed:",
      persistenceReport,
    );
  }

  return componentLoadingStrategy;
}
